import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Save, ArrowLeft } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../lib/api';
import { healthConcernLabel, calculateBMI, getBMICategory } from '../lib/utils';
import { toast } from 'sonner';

const healthConcerns = [
  { id: 'fertility', label: 'Fertility' },
  { id: 'gut_health', label: 'Gut Health' },
  { id: 'vaginal_health', label: 'Vaginal Health' },
  { id: 'chronic_pain', label: 'Chronic Pain' },
  { id: 'inflammation', label: 'Inflammation' },
  { id: 'longevity', label: 'Longevity' },
  { id: 'preventative_care', label: 'Preventative Care' },
];

const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown'];
const sexOptions = ['female', 'male', 'other', 'prefer_not_to_say'];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const profile = user?.profile || {};
  
  const [formData, setFormData] = useState({
    health_concerns: profile.health_concerns || [],
    age: profile.age?.toString() || '',
    sex: profile.sex || '',
    height_cm: profile.height_cm?.toString() || '',
    weight_kg: profile.weight_kg?.toString() || '',
    blood_type: profile.blood_type || '',
    dietary_restrictions: (profile.dietary_restrictions || []).join(', '),
    allergies: (profile.allergies || []).join(', '),
  });
  const [loading, setLoading] = useState(false);

  const toggleConcern = (concernId) => {
    setFormData(prev => ({
      ...prev,
      health_concerns: prev.health_concerns.includes(concernId)
        ? prev.health_concerns.filter(c => c !== concernId)
        : [...prev.health_concerns, concernId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        health_concerns: formData.health_concerns,
        age: parseInt(formData.age),
        sex: formData.sex,
        height_cm: parseFloat(formData.height_cm),
        weight_kg: parseFloat(formData.weight_kg),
        blood_type: formData.blood_type || 'unknown',
        dietary_restrictions: formData.dietary_restrictions
          ? formData.dietary_restrictions.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        allergies: formData.allergies
          ? formData.allergies.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };

      const response = await usersAPI.updateProfile(data);
      updateUser(response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const bmi = calculateBMI(parseFloat(formData.height_cm), parseFloat(formData.weight_kg));

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="profile-page">
        <div className="soft-card p-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#E8A095]/20 flex items-center justify-center">
              <User className="w-8 h-8 text-[#E8A095]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-[#4A2C2A]">{user?.name}</h1>
              <p className="text-[#4A2C2A]/60">{user?.email}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Health Concerns */}
            <div>
              <Label className="text-[#4A2C2A] mb-3 block">Health Focus Areas</Label>
              <div className="flex flex-wrap gap-2">
                {healthConcerns.map((concern) => {
                  const isSelected = formData.health_concerns.includes(concern.id);
                  return (
                    <button
                      key={concern.id}
                      type="button"
                      onClick={() => toggleConcern(concern.id)}
                      className={`health-chip ${isSelected ? 'selected' : ''}`}
                      data-testid={`profile-concern-${concern.id}`}
                    >
                      {concern.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Personal Info */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#4A2C2A]">Age</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  className="input-warm"
                  data-testid="profile-age-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#4A2C2A]">Sex</Label>
                <Select
                  value={formData.sex}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, sex: value }))}
                >
                  <SelectTrigger className="input-warm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {sexOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#4A2C2A]">Height (cm)</Label>
                <Input
                  type="number"
                  value={formData.height_cm}
                  onChange={(e) => setFormData(prev => ({ ...prev, height_cm: e.target.value }))}
                  className="input-warm"
                  data-testid="profile-height-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#4A2C2A]">Weight (kg)</Label>
                <Input
                  type="number"
                  value={formData.weight_kg}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight_kg: e.target.value }))}
                  className="input-warm"
                  data-testid="profile-weight-input"
                />
              </div>
            </div>

            {/* BMI Display */}
            {bmi && (
              <div className="bg-[#FDFBF7] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <span className="text-sm text-[#4A2C2A]/60">Your BMI</span>
                  <p className="font-heading text-xl text-[#4A2C2A]">{bmi}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  getBMICategory(bmi) === 'Normal' 
                    ? 'bg-[#8DA399]/20 text-[#8DA399]'
                    : 'bg-[#E8C495]/20 text-[#E8C495]'
                }`}>
                  {getBMICategory(bmi)}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[#4A2C2A]">Blood Type</Label>
              <Select
                value={formData.blood_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, blood_type: value }))}
              >
                <SelectTrigger className="input-warm">
                  <SelectValue placeholder="Select blood type" />
                </SelectTrigger>
                <SelectContent>
                  {bloodTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'unknown' ? "I don't know" : type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#4A2C2A]">Dietary Restrictions</Label>
              <Input
                value={formData.dietary_restrictions}
                onChange={(e) => setFormData(prev => ({ ...prev, dietary_restrictions: e.target.value }))}
                placeholder="e.g., Vegetarian, Vegan, Gluten-free"
                className="input-warm"
                data-testid="profile-dietary-input"
              />
              <p className="text-xs text-[#4A2C2A]/50">Comma-separated</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#4A2C2A]">Allergies</Label>
              <Input
                value={formData.allergies}
                onChange={(e) => setFormData(prev => ({ ...prev, allergies: e.target.value }))}
                placeholder="e.g., Nuts, Dairy, Shellfish"
                className="input-warm"
                data-testid="profile-allergies-input"
              />
              <p className="text-xs text-[#4A2C2A]/50">Comma-separated</p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl px-8"
                data-testid="profile-save-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" /> Save Changes
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
